/**
 * Fluxo "Atender agora":
 * paciente → detalhes → Confirmar (cria) → ações + Iniciar consulta.
 */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { formatBrlInput } from "./moneyUtils";
import {
  ProntoAtendimentoPatientStep,
  type ProntoSelectedPatient,
} from "./ProntoAtendimentoPatientStep";
import { ProntoAtendimentoQueueActions } from "./ProntoAtendimentoQueueActions";
import {
  useProntoAtendimentoAppointment,
} from "./useProntoAtendimentoAppointment";
import type { ProntoActionAppointment } from "./mapProntoAppointmentToQueueItem";
import {
  PRONTO_ATENDIMENTO_MODE_LABELS,
  type ProntoAtendimentoConfig,
} from "./types";

type Step = "patient" | "details" | "actions";

interface ProntoAtendimentoAttendNowFlowProps {
  open: boolean;
  onClose: () => void;
  config: ProntoAtendimentoConfig | null;
  onSuccess?: () => void;
}

export function ProntoAtendimentoAttendNowFlow({
  open,
  onClose,
  config,
  onSuccess,
}: ProntoAtendimentoAttendNowFlowProps) {
  const [step, setStep] = useState<Step>("patient");
  const [patient, setPatient] = useState<ProntoSelectedPatient | null>(null);
  const [createdAppointment, setCreatedAppointment] =
    useState<fhir5.Appointment | null>(null);
  const [actionAppointment, setActionAppointment] =
    useState<ProntoActionAppointment | null>(null);

  const { createAppointment, startEncounter, loading, starting } =
    useProntoAtendimentoAppointment();
  const { hasFluxPayExtension } = useFluxPayContractContext();

  useEffect(() => {
    if (!open) return;
    setStep("patient");
    setPatient(null);
    setCreatedAppointment(null);
    setActionAppointment(null);
  }, [open]);

  const canConfirm =
    !!patient &&
    !!config?.active &&
    !!config.id &&
    (!hasFluxPayExtension ||
      (Number.isFinite(config.value) && config.value > 0));

  const handlePatientContinue = (p: ProntoSelectedPatient) => {
    setPatient(p);
    setStep("details");
  };

  /** Confirma = só cria a consulta; não inicia Encounter. */
  const handleConfirm = async () => {
    if (!patient || !config?.active) {
      toast.info("Ative o Pronto Atendimento antes de confirmar.");
      return;
    }

    const result = await createAppointment({
      patient,
      config,
      value: config.value,
    });

    if (result) {
      setCreatedAppointment(result.appointment);
      setActionAppointment(result.actionAppointment);
      setStep("actions");
      onSuccess?.();
    }
  };

  const handleStart = async () => {
    if (!createdAppointment) return;
    const ok = await startEncounter(createdAppointment);
    if (ok) {
      onSuccess?.();
      onClose();
    }
  };

  const handleClose = () => {
    // Se já confirmou e fecha, a consulta fica na fila
    if (step === "actions") {
      onSuccess?.();
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {step === "actions" ? "Atendimento confirmado" : "Atender agora"}
      </DialogTitle>
      <DialogContent dividers>
        {step === "patient" ? (
          <ProntoAtendimentoPatientStep onContinue={handlePatientContinue} />
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Paciente: {patient?.display}
                </Typography>
                {patient?.cpf && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    CPF: {patient.cpf}
                  </Typography>
                )}
                {patient?.responsibleName && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Responsável: {patient.responsibleName}
                  </Typography>
                )}
              </Box>
              {step === "details" && (
                <Button size="small" onClick={() => setStep("patient")}>
                  Trocar paciente
                </Button>
              )}
            </Stack>

            {config ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: "primary100.main",
                  borderColor: "primary.main",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Agenda de Pronto Atendimento
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {config.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {config.specialty || config.healthcareServiceDisplay
                    ? `${config.specialty || config.healthcareServiceDisplay} · `
                    : ""}
                  {PRONTO_ATENDIMENTO_MODE_LABELS[config.mode]}
                  {hasFluxPayExtension && config.value > 0
                    ? ` · R$ ${formatBrlInput(config.value)}`
                    : ""}
                  {config.locationDisplay
                    ? ` · ${config.locationDisplay}`
                    : ""}
                </Typography>
              </Paper>
            ) : (
              <Typography variant="body2" color="error">
                Nenhuma agenda ativa. Feche e ative o Pronto Atendimento.
              </Typography>
            )}

            {step === "actions" && actionAppointment && (
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  Consulta marcada. Envie lembrete, WhatsApp ou questionários, ou
                  inicie o atendimento.
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  useFlexGap
                  spacing={1}
                >
                  <ProntoAtendimentoQueueActions
                    appointment={actionAppointment}
                    onRefetch={onSuccess}
                    onCancelled={handleClose}
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading || starting}
          sx={{ textTransform: "none" }}
        >
          {step === "actions" ? "Fechar" : "Cancelar"}
        </Button>
        {step === "details" && (
          <Button
            variant="contained"
            onClick={() => void handleConfirm()}
            disabled={!canConfirm || loading}
            sx={{ textTransform: "none" }}
          >
            {loading ? "Confirmando..." : "Confirmar atendimento"}
          </Button>
        )}
        {step === "actions" && (
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={() => void handleStart()}
            disabled={starting || !createdAppointment}
            sx={{ textTransform: "none" }}
          >
            {starting ? "Iniciando..." : "Iniciar consulta"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
